import type { CalendarEvent } from 'src/meetings/models';

type ClientGuest = {
  name: string;
  participantType: 'CLIENT';
  clientId: string;
};

type UserGuest = {
  name: string;
  participantType: 'USER';
  userId: string;
};

type GuestDisplayGuest = ClientGuest | UserGuest;

type GuestDisplayData = {
  totalGuests: number;
  knownGuests: GuestDisplayGuest[];
  unknownCount: number;
};

const buildFullName = (firstName?: string | null, lastName?: string | null): string | undefined => {
  const parts = [firstName, lastName].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : undefined;
};

export const getGuestDisplayData = (event: CalendarEvent): GuestDisplayData => {
  const knownGuests = event.eventParticipants.flatMap<GuestDisplayGuest>(participant => {
    const { client, user } = participant.participant;

    if (client) {
      const name = buildFullName(client.firstName, client.lastName);
      return name
        ? [
            {
              name,
              participantType: 'CLIENT',
              clientId: client.id,
            },
          ]
        : [];
    }

    if (user) {
      const name = buildFullName(user.firstName, user.lastName);
      return name
        ? [
            {
              name,
              participantType: 'USER',
              userId: user.id,
            },
          ]
        : [];
    }

    return [];
  });

  const totalGuests = event.eventParticipants.length;
  const unknownCount = Math.max(totalGuests - knownGuests.length, 0);

  return {
    totalGuests,
    knownGuests,
    unknownCount,
  };
};

export const getFirstClientParticipant = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'][0] | undefined => {
  return event.eventParticipants.find(p => p.participant.client !== null);
};

export const getAllClientParticipants = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'] => {
  return event.eventParticipants.filter(p => p.participant.client !== null);
};

export type ParticipantFamily = { id: string; name: string };

/**
 * Display names for a meeting's attendees — advisers (user participants),
 * clients, and external attendees (those added by name).
 *
 * When `familiesByClientId` is supplied and two or more client attendees belong
 * to the same family, that group collapses to a single "<name> family" label
 * (placed where the family's first attendee appears) instead of listing each
 * member individually.
 */
export const getParticipantDisplayNames = (
  event: CalendarEvent,
  familiesByClientId?: Map<string, ParticipantFamily>,
): string[] => {
  const familyMemberCounts = new Map<string, number>();
  if (familiesByClientId) {
    for (const { participant } of event.eventParticipants) {
      const family = participant.client && familiesByClientId.get(participant.client.id);
      if (family) {
        familyMemberCounts.set(family.id, (familyMemberCounts.get(family.id) ?? 0) + 1);
      }
    }
  }

  const emittedFamilyIds = new Set<string>();
  const names: string[] = [];

  for (const { participant } of event.eventParticipants) {
    if (participant.client) {
      const family = familiesByClientId?.get(participant.client.id);
      if (family && (familyMemberCounts.get(family.id) ?? 0) >= 2) {
        if (!emittedFamilyIds.has(family.id)) {
          emittedFamilyIds.add(family.id);
          names.push(`${family.name} family`);
        }
        continue;
      }
      const name = buildFullName(participant.client.firstName, participant.client.lastName);
      if (name) names.push(name);
      continue;
    }
    if (participant.user) {
      const name = buildFullName(participant.user.firstName, participant.user.lastName);
      if (name) names.push(name);
      continue;
    }
    if (participant.participantType === 'EXTERNAL' && participant.name) {
      names.push(participant.name);
    }
  }

  return names;
};

/**
 * Names of the clients linked to a meeting. Used on the practice-wide hub to
 * show which client(s) a meeting belongs to, since the list spans everyone.
 */
export const getClientDisplayNames = (event: CalendarEvent): string[] =>
  getAllClientParticipants(event)
    .map(({ participant }) =>
      buildFullName(participant.client?.firstName, participant.client?.lastName),
    )
    .filter((name): name is string => name !== undefined);

export const getParticipantFullName = (
  participant: CalendarEvent['eventParticipants'][0] | undefined,
): string | undefined => {
  if (!participant) {
    return undefined;
  }

  const { client, user } = participant.participant;

  if (client) {
    return buildFullName(client.firstName, client.lastName);
  }

  if (user) {
    return buildFullName(user.firstName, user.lastName);
  }

  return undefined;
};
